import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./CheckoutModal.css";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import DeliveryForm from "./DeliveryForm";
import { useApi } from "../utils/Api";
import ErrorModal from "./ErrorModal";

/* -------- Spinner Overlay -------- */
const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

function CheckoutModal({ onClose = () => {}, isOpen, checkoutType }) {
  const { apiFetch } = useApi();
  const navigate = useNavigate();

  const [stage, setStage] = useState(0);
  const [selectedCheckoutType, setSelectedCheckoutType] = useState(checkoutType || null);
  const [tableNumber, setTableNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { cartItems, clearCart } = useCart();
  const { user, openLoginModal } = useUser();

  const [encryptedOrderId, setEncryptedOrderId] = useState(null);

  // --- Service Charge ---
  const SERVICE_CHARGE_AMOUNT = 2.5;
  const [addServiceCharge, setAddServiceCharge] = useState(false);

  const allowedServiceChargePhones = ["+447495891188"];
  const canAddServiceCharge =
    user && allowedServiceChargePhones.includes(user.phone);

  // Compute cart total
  const getItemTotals = (cartItem) => {
    const item = cartItem.item || cartItem;
    let total = Number(item.price || 0);

    if (cartItem.selectedRequiredOption) {
      total += Number(cartItem.selectedRequiredOption.price || 0);
    }

    [
      ...(item.swallowOptions || []),
      ...(item.proteins || []),
      ...(cartItem.selectedOptionalOptions || []),
    ].forEach((opt) => {
      total += Number(opt.option_price || 0) * (opt.quantity || 1);
    });

    return { total };
  };

  const cartTotal = cartItems.reduce(
    (acc, c) => acc + getItemTotals(c).total * (c.quantity || 1),
    0
  );

  // Total including service charge
  const cartTotalWithService =
    cartTotal + (addServiceCharge ? SERVICE_CHARGE_AMOUNT : 0);

  // Helper for full name
  const getUserFullName = () =>
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  // Reset modal when closed
  useEffect(() => {
    if (!isOpen) {
      setStage(0);
      setSelectedCheckoutType(null);
      setTableNumber("");
      setNotes("");
      setSuccess(false);
      setIsLoading(false);
      setAddServiceCharge(false);
    } else {
      setStage(checkoutType ? 1 : 0);
      if (checkoutType) setSelectedCheckoutType(checkoutType);
    }
  }, [isOpen, checkoutType]);

  const goBack = () => {
    if (stage > 1) setStage(1);
    else if (stage === 1 && !selectedCheckoutType) setStage(0);
  };

  const handleSelectOrderType = (type) => {
    setSelectedCheckoutType(type);
    setStage(1);
  };

  const handleEatInStage1Answer = (answer) => {
    setStage(answer ? 2 : 3);
  };

  const placeEatInOrPickupOrder = async () => {
    if (isLoading) return;
    setIsLoading(true);

    if (!cartItems.length) {
      setIsLoading(false);
      return;
    }

    if (!user) {
      openLoginModal();
      setIsLoading(false);
      return;
    }

    const payload = {
      customer_id: user.id,
      order_type: selectedCheckoutType === "eat-in" ? "Eat In" : "Pick Up",
      table_number:
        selectedCheckoutType === "eat-in" ? tableNumber.trim() : null,
      notes: notes.trim(),
      items: cartItems.map((c) => ({
        item_id: c.item?.id,
        base_price: c.item?.price || 0,
        required_option_id: c.selectedRequiredOption?.id || null,
        required_option_name: c.selectedRequiredOption?.name || "",
        required_option_price: c.selectedRequiredOption?.price || 0,
        quantity: c.quantity || 1,
        selectedOptionalOptions: c.selectedOptionalOptions || [],
        swallowOptions: c.item?.swallowOptions || [],
        proteins: c.item?.proteins || [],
      })),
      total_price: cartTotalWithService,
      service_charge: addServiceCharge ? SERVICE_CHARGE_AMOUNT : 0,
      status: "to do",
    };

    try {
      const result = await apiFetch("place-order", payload);
      if (result.success) {
        setEncryptedOrderId(result.encrypted_order_id);
        setSuccess(true);
        setTimeout(() => clearCart(), 1000);
      } else {
        console.error("Order failed:", result.error);
      }
    } catch (err) {
      console.error("Error sending order:", err);
    }

    setIsLoading(false);
  };

  const canSubmitEatIn = tableNumber.trim() !== "" && !isLoading;
  const canSubmitPickup = !isLoading;

  // ---------- Immediate delivery block check ----------
  const BLOCKED_DELIVERY_IDS = [9, 11, 12, 16];
  const blockedDeliveryItem = cartItems.find((c) =>
    BLOCKED_DELIVERY_IDS.includes(Number(c.item?.category_id))
  );
  const deliveryBlocked =
    selectedCheckoutType === "delivery" && stage === 1 && blockedDeliveryItem;

  if (!isOpen) return null;

  return (
    <div className="checkout-modal-overlay">
      <div className="checkout-modal" style={{ position: "relative" }}>
        {isLoading && <SpinnerOverlay />}

        <div className="checkout-modal-top modal-top">
          {stage > 1 && !success ? (
            <i className="fas fa-arrow-left back-icon" onClick={goBack}></i>
          ) : (
            <div className="back-placeholder"></div>
          )}
          <button
            className="add-to-cart-button close-modal-button"
            onClick={onClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <h2 className="checkout-heading">
          {!success && stage === 0 && "Select Order Type"}
          {!success &&
            selectedCheckoutType === "eat-in" &&
            stage === 1 &&
            "Eat In Checkout"}
          {!success &&
            selectedCheckoutType === "pickup" &&
            stage === 1 &&
            "Pick Up Checkout"}
          {!success &&
            selectedCheckoutType === "eat-in" &&
            stage === 2 &&
            "Customer Information"}
          {!success &&
            selectedCheckoutType === "eat-in" &&
            stage === 3 &&
            "Cannot Order"}
          {!success &&
            selectedCheckoutType === "delivery" &&
            stage === 1 &&
            "Delivery Checkout"}
          {success && "Order Received"}
        </h2>
        

        {/* Stage 0 */}
        {!success && stage === 0 && (
          <div className="stage-content">
            <h3>How would you like your order?</h3>
            <div className="button-group">
              <button
                className="add-to-cart-button"
                onClick={() => handleSelectOrderType("eat-in")}
              >
                Eat In
              </button>
              <button
                className="add-to-cart-button"
                onClick={() => handleSelectOrderType("pickup")}
              >
                Pick Up
              </button>
              {/* <button
                className="add-to-cart-button"
                onClick={() => handleSelectOrderType("delivery")}
              >
                Delivery
              </button> */}
            </div>
          </div>
        )}

        {/* Eat-in Stage 1 */}
        {!success && selectedCheckoutType === "eat-in" && stage === 1 && (
          <div className="stage-content">
            <h3>Are you currently in the restaurant?</h3>
            <div className="button-group">
              <button
                className="add-to-cart-button"
                onClick={() => handleEatInStage1Answer(true)}
              >
                Yes
              </button>
              <button
                className="add-to-cart-button"
                onClick={() => handleEatInStage1Answer(false)}
              >
                No
              </button>
            </div>
          </div>
        )}

        {/* Eat-in Stage 2 */}
        {!success && selectedCheckoutType === "eat-in" && stage === 2 && (
          <form
            className="checkout-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmitEatIn) placeEatInOrPickupOrder();
            }}
          >
            <label>Your Name</label>
            <input type="text" value={getUserFullName()} readOnly required />
            <label>Table Number</label>
            <input
              type="text"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              required
            />
            <label>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {/* Service charge toggle */}
            {/* Service charge toggle */}
            {canAddServiceCharge && (
              <div className="service-charge-toggle">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={addServiceCharge}
                    onChange={() => setAddServiceCharge(!addServiceCharge)}
                  />
                  <span className="slider round"></span>
                </label>
                <span style={{ marginLeft: "10px" }}>
                  Service Charge (£{SERVICE_CHARGE_AMOUNT.toFixed(2)})
                </span>
              </div>
            )}

            {addServiceCharge && (
              <div className="price-breakdown" style={{ margin: "1rem 0" }}>
                <div className="price-row">
                  <span>Order Price</span>
                  <span>£{cartTotal.toFixed(2)}</span>
                </div>

                <div className="price-row">
                  <span>Service Charge</span>
                  <span>£{SERVICE_CHARGE_AMOUNT.toFixed(2)}</span>
                </div>

                <hr />

                <div className="price-row total">
                  <span>
                    <strong>Total</strong>
                  </span>
                  <span>
                    <strong>£{cartTotalWithService.toFixed(2)}</strong>
                  </span>
                </div>
              </div>
            )}
            <button
              type="submit"
              className="add-to-cart-button send-button"
              disabled={!canSubmitEatIn}
            >
              Send Order To Kitchen
              <span style={{ marginLeft: "0.9rem", fontWeight: "bold" }}>
                £{cartTotalWithService.toFixed(2)}
              </span>
            </button>
          </form>
        )}

        {!success && selectedCheckoutType === "eat-in" && stage === 3 && (
          <div className="cannot-order-message">
            <p>You must be in the restaurant to place an Eat-In order.</p>
          </div>
        )}

        {/* Pickup Stage */}
        {!success && selectedCheckoutType === "pickup" && stage === 1 && (
          <form
            className="checkout-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmitPickup) placeEatInOrPickupOrder();
            }}
          >
            <label>Your Name</label>
            <input type="text" value={getUserFullName()} readOnly required />

            <label>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <button
              type="submit"
              className="add-to-cart-button send-button"
              disabled={!canSubmitPickup}
            >
              Send Order To Kitchen
              <span style={{ marginLeft: "0.9rem", fontWeight: "bold" }}>
                £{cartTotalWithService.toFixed(2)}
              </span>
            </button>
          </form>
        )}

        {/* Delivery Stage */}
        {!success &&
          selectedCheckoutType === "delivery" &&
          stage === 1 &&
          !deliveryBlocked && (
            <DeliveryForm
              notes={notes}
              setNotes={setNotes}
              setIsLoading={setIsLoading}
              onClose={onClose} 
            />
          )}

        {/* Success */}
        {success && (
          <div className="order-success">
            <h3>Your order has been received and is being processed.</h3>

            <button
              className="add-to-cart-button"
              onClick={() => {
                if (encryptedOrderId) {
                  navigate(`/order-tracking?id=${encryptedOrderId}`);
                }
                onClose(); 
              }}
            >
              Click here to track your order
            </button>
          </div>
        )}

        {/* Error Modal */}
        {deliveryBlocked && (
          <ErrorModal
            message="Alcoholic drinks cannot be delivered. Please remove them to proceed with delivery."
            onClose={() => {
              setSelectedCheckoutType(null);
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default CheckoutModal;
